import * as React from 'react';
import { connect } from 'react-redux';
import actions, { ActionRequest } from '../actions';
import { ContainerContext, mapProps } from './index';
import { UserRole, UserTitleRole } from '../enums';
import { formatDate } from '../utils';
import {
    BaseContainer,
    BaseElement,
    CreateUserDialog,
    WarningDialog,
    Table,
    HeaderButton,
} from '../components';
import {
    AddOutlined,
    DeleteForeverOutlined, SyncOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { Avatar, Grid2 as Grid } from '@mui/material';
import { StateMap } from '../reducers';
import useMediaQuery from '@mui/material/useMediaQuery';

interface Props extends ContainerContext.Props {
}

type DialogType = 'Create' | 'Delete' | 'Refresh';
interface State {
    readonly openDialog: DialogType | null;
    readonly selectedUser: Entities.User | null;
}

class Container extends BaseElement<Props, State> {
    state: State = {
        openDialog: null,
        selectedUser: null,
    };

    async componentDidMount() {
        actions.title.resetTitle();
    }

    protected renderContainer(): React.JSX.Element {
        const { loading } = this.props;
        const buttons: HeaderButton[] | undefined = this.props.userTitleRole === UserTitleRole.Viewer ? undefined : [
            { text: 'Create User', icon: AddOutlined, onClick: this.onCreateAdminUser }
        ];
        return (
            <BaseContainer
                loading = {loading}
                styled={this.props.styled}
                title = ""
                buttons = {buttons}
            >
                {this.renderContent()}
            </BaseContainer>
        );
    }

    protected renderDialogs(): React.JSX.Element {
        return (
            <>
                {this.renderDialog()}
                {this.renderConfirmDeleteDialog()}
            </>
        );
    }

    private onCreateAdminUser = () => this.setState({ openDialog: 'Create' });

    private renderContent = () => {
        const { userRole } = this.props;
        const columns: any = [
            { title: 'User', field: 'username', defaultGroupSort: 'asc'},
            { title: 'Role', field: 'role'},
            { title: 'Type', field: 'loginType'},
            { title: 'Last Login', field: 'lastLogin'},
            { title: 'Created', field: 'created'},
        ];
        return (
            <Table
                styled={this.props.styled}
                data={this.getData}
                columns={columns}
                onRowClick={this.onRowClick}
                options={{
                    showTitle: false,
                    selection: false,
                    paging: true,
                    pageSize: 10,
                    pageSizeOptions: [5, 10, 30, 50, 100],
                    search: true,
                    sorting: true,
                    draggable: false,
                    actionsColumnIndex: -1,
                    emptyRowsWhenPaging: false,
                }}
                actions={ userRole > UserRole.Developer ?
                    [
                        rowData => ({
                            icon: SyncOutlined,
                            tooltip: 'Refresh Titles',
                            onClick: () => this.onRefreshTitles(rowData.user)
                        }),
                        rowData => ({
                            icon: DeleteForeverOutlined,
                            tooltip: 'Delete',
                            onClick: () => this.setState({ openDialog: 'Delete', selectedUser: rowData.user})
                        }),
                    ]
                    : undefined
                }
            />
        );
    };

    private getData = async (query: any) => {
        const { search, page, pageSize, orderBy, orderDirection } = query;
        const result = await ActionRequest.get(`users/list`, {
            search,
            page: page + 1,
            perPage: pageSize,
            orderBy: orderBy ? orderBy.field : null,
            orderDirection: orderDirection !== "" ? orderDirection : null,
        });
        if (!result) {
            return {
                data: [],
                page: 0,
                totalCount: 0,
            };
        }

        const data = result.entities.map((user: Entities.User) => ({
            username: this.getUser(user),
            userName: user.username,
            role: UserRole[user.role],
            loginType: user.loginType,
            lastLogin: formatDate(user.lastLogin),
            created: formatDate(user.created),
            user,
        }));

        return {
            data,
            page: result.page - 1,
            totalCount: result.totalCount,
        };
    };

    private getUser = (user: Entities.User) => {
        const { username, name, picture } = user;

        return (
            <Grid container={true} justifyContent="center" alignItems="center" spacing={0}>
                <Grid size={12} style={{ display: 'grid', gridColumnGap: 10, gridAutoFlow: 'column', justifyContent: 'flex-start'}}>
                    <Avatar
                        alt="Remy Sharp"
                        src={picture}
                        sx={{
                            height: 40,
                            width: 40,
                            backgroundColor: 'rgba(0,0,0,0.025)',
                        }}
                    />
                    {name !== '' && (
                        <Grid container={true}>
                            <Grid size={12}>
                                <div style={{ color: 'rgba(0,0,0,.87)', fontSize: 14 }} >
                                    {name}
                                </div>
                            </Grid>
                            <Grid size={12}>
                                <div style={{  color: 'rgba(0,0,0,.54)', fontSize: 12 }} >
                                    {username}
                                </div>
                            </Grid>
                        </Grid>
                    )}
                    {name === '' && (
                        <Grid container={true}>
                            <Grid size={12} >
                                <div style={{ color: 'rgba(0,0,0,.87)', fontSize: 14 }} >
                                    {username}
                                </div>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </Grid>
        );
    }

    private onRowClick = (event: any, rowData: any) => {
        const selectedUser = rowData.user;
        if (selectedUser) {
            this.setState({ openDialog: 'Create', selectedUser });
        }
    };

    private renderDialog() {
        const { loginUser } = this.props;
        const { openDialog, selectedUser } = this.state;
        const title = loginUser === selectedUser ? 'Update My Information' : 'Create Admin User';
        if (!loginUser) {
            return null;
        }
        return (
            <CreateUserDialog
                styled={this.props.styled}
                open = {openDialog === 'Create'}
                title={title}
                loginUser={loginUser}
                user={selectedUser}
                onClose={this.closeDialog}
                onCreate={selectedUser ? this.updateUser : this.createUser}
            />
        );
    }

    private renderConfirmDeleteDialog = () => {
        const { openDialog, selectedUser } = this.state;
        if (!selectedUser) {
            return;
        }
        return (
            <WarningDialog
                styled={this.props.styled}
                open={openDialog === 'Delete'}
                title="Delete"
                content={`This will permanently delete '${selectedUser.name}' user.`}
                onClose={this.closeDialog}
                onSubmit={this.onDeleteUser}
                maxWidth={'xs'}
            />
        );
    };
    private onDeleteUser = async () => {
        const { selectedUser } = this.state;
        this.closeDialog();
        if (!selectedUser) {
            return;
        }
        await actions.admin.deleteUser(selectedUser);
    }

    private closeDialog = () => this.setState({ openDialog: null, selectedUser: null });

    private createUser = async (user: Entities.BaseUser) => {
        this.closeDialog();
        await actions.admin.createUser(user);
    };

    private updateUser = async (user: Entities.BaseUser) => {
        this.closeDialog();
        await actions.admin.updateUser(user);
    };

    private onRefreshTitles = async (user: Entities.BaseUser) => {
        await actions.admin.refreshTitles(user);
    }
}

const mapStateToProps = (state: StateMap): Props => ({
    ...mapProps(state),
});
const AppContainer = (props: Props) =>
{
    const smallUp = useMediaQuery((theme) => theme.breakpoints.up('sm'));
    const navigate = useNavigate();
    return (<Container {...props} styled={{themeMode: props.app.themeMode, smallUp}} navigate={navigate}/>);
};
export default connect(mapStateToProps)(AppContainer);
